import { EventEmitter } from "ui/video/call/eventEmitter";
import { DeviceId, CallId, SocketId, GroupId, UtcTime } from 'shared/models';
import moment from 'moment';
import UUID from "util/UUID";

// Ping the server once every X milliseconds so we know the connection is still alive
const PING_INTERVAL = 30000;
const PONG_TIMEOUT = 5000;

export type MessageHeader = {
    scope: string;
    type: string;
    id: string;
};

/* eslint-disable-next-line @typescript-eslint/naming-convention -- Name needs to match to get the on() typing correct */
export interface WebSocketClient {
    on(event: 'message', listener: <M extends MessageHeader>(msg: M) => void): WebSocketClient;
    on(event: 'reauth', listener: () => void): WebSocketClient;
    on(event: 'connecting', listener: () => void): WebSocketClient;
    on(event: 'connected', listener: () => void): WebSocketClient;
    on(event: 'disconnected', listener: () => void): WebSocketClient;
}

export class WebSocketClient extends EventEmitter {
    host: string;
    websocket: WebSocket | null;
    failures: number = 0;
    enabled: boolean = false;
    queue: MessageHeader[] = [];
    retryTimer: NodeJS.Timeout | null = null;
    pingTimer: NodeJS.Timeout | null = null;
    lastPingTime: number = 0;
    lastPongTime: number = 0;
    socketId: SocketId;
    everOpened: boolean = false;

    constructor(host: string) {
        super();
        this.host = host;
        this.websocket = null;
        this.socketId = UUID.generate();
    }

    enable = (enabled: boolean) => {
        this.enabled = enabled;
        if (enabled) {
            this.failures = 0;
            this.everOpened = false;
            this.connect();
        }
        else {
            this.close();
        }
    };

    get connected() {
        return !!(this.websocket && this.websocket.readyState === WebSocket.OPEN);
    }

    get connecting() {
        return !!(this.websocket && this.websocket.readyState === WebSocket.CONNECTING);
    }

    connect() {
        clearTimeout(this.retryTimer!);
        this.retryTimer = null;

        clearInterval(this.pingTimer!);
        this.pingTimer = null;

        if (!this.enabled) return;
        if (this.websocket && (this.websocket.readyState === WebSocket.OPEN || this.websocket.readyState === WebSocket.CONNECTING)) {
            return;
        }
        try {
            this.emit('connecting');
            this.websocket = new WebSocket(`wss://${this.host}/api/wss/webclient/${this.socketId}`);
        }
        catch (err) {
            console.error('Failed to connect to websocket', err);
            return;
        }
        this.websocket.onerror = () => {
            if (this.failures === 0) {
                console.error('Failed to connect to websocket');
            }
        };
        this.websocket.onopen = () => {
            this.failures = 0;
            this.everOpened = true;
            console.log(`Connected to websocket at ${moment.utc().format('H:mm:ss:SSS')}`);

            this.emit('connected');

            this.lastPingTime = 0;
            this.lastPongTime = 0;
            this.pingTimer = setInterval(() => {
                if (!this.websocket) {
                    return;
                }
                if (this.lastPingTime - this.lastPongTime > PONG_TIMEOUT) {
                    clearInterval(this.pingTimer!);
                    this.websocket.close(4504, 'pong timeout');
                    return;
                }
                if (Date.now() - this.lastPingTime >= PING_INTERVAL) {
                    this.lastPingTime = Date.now();
                    this.websocket.send('PING');
                }
            }, 1000);

            if (this.queue.length) {
                console.debug(`Sending ${this.queue.length} queued messages...`);
            }
            while (this.queue.length > 0) {
                const msg = this.queue.shift()!;
                this.send(msg, false);
            }
        };
        this.websocket.onmessage = ev => {
            if (ev.data === 'PONG') {
                this.lastPongTime = Date.now();
                return;
            }
            let msg;
            try {
                msg = JSON.parse(ev.data);
            }
            catch (err) {
                console.error(`Failed to parse message ${ev.data}`, err);
                return;
            }
            this.emit('message', msg);
            
            if (this.websocket && msg.id) {
                this.websocket.send(JSON.stringify({
                    type: 'ACK',
                    sid: msg.id,
                }));
            }
        };
        this.websocket.onclose = e => {
            this.emit('disconnected');

            clearInterval(this.pingTimer!);
            this.pingTimer = null;

            // Don't attempt a reconnect if the client is disabled (e.g. user has logged out)
            if (!this.enabled) return;

            // Request that the caller reauthenticates because we're possibly missing
            // the session cookie in the websocket requests.
            if (!this.everOpened && this.failures > 10) {
                this.enabled = false;
                this.emit('reauth');
                return;
            }

            // Retry connecting to the server
            const { code, reason } = e;
            if (this.failures === 0) {
                console.log(`Websocket closed at ${moment.utc().format('H:mm:ss:SSS')}: ${code} - ${reason || '(no reason)'}`);
            }
            const delay = Math.min(125 * Math.pow(1.5, Math.min(this.failures++, 11)), 10000);
            console.debug(`Attempting reconnect in ${Math.round(delay) / 1000}s`);
            this.retryTimer = setTimeout(() => {
                this.connect();
            }, delay);
        };
    }

    close = () => {
        if (!this.websocket) {
            return;
        }
        try { this.websocket.close(1000, 'normal'); }
        catch { /* swallow the error */ }
        this.websocket = null;
    };

    private send(msg: MessageHeader, queueIfNotOpen: boolean = true) {
        if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
            if (queueIfNotOpen) {
                console.warn(`WebSocket is not open yet (${this.websocket ? this.websocket.readyState : 'not created'}). Saving ${msg.scope}/${msg.type} for later`);
                this.queue.push(msg);
                if (!this.websocket || this.websocket.readyState !== WebSocket.CONNECTING) {
                    this.connect();
                }
            }
            else {
                console.warn(`WebSocket is not open yet (${this.websocket ? this.websocket.readyState : 'not created'}). Discarding ${msg.scope}/${msg.type}`);
            }
        }
        else {
            this.websocket.send(JSON.stringify(msg));
        }
    }

    private sendFtp(type: string, msg: Record<string, unknown> = {}) {
        this.send({
            id: UUID.generate(),
            scope: 'FTP',
            type,
            ...msg,
        });
    }

    public ftpRead(deviceId: DeviceId, filename: string) {
        this.sendFtp('READ', {
            deviceId,
            filename,
        });
    }

    public ftpWrite(deviceId: DeviceId, filename: string, content: string) {
        this.sendFtp('WRIT', {
            deviceId,
            filename,
            content: btoa(content),
        });
    }


    private sendEcs(type: string, msg: Record<string, unknown> = {}, queueIfNotOpen: boolean = true) {
        this.send({
            id: UUID.generate(),
            scope: 'ECS',
            type,
            ...msg,
        }, queueIfNotOpen);
    }

    public ecsRegister(): void {
        this.sendEcs('STRT');
    }

    public ecsUnregister(): void {
        this.sendEcs('STOP');
    }

    private lastCallRequestTimeByElevatorId: Record<GroupId, UtcTime> = {};

    public ecsCall(elevatorId: GroupId, enqueueIfNotOpen: boolean): void {
        // M1-607: Calvin has seen a few times that the MSCL message gets sent twice about 40ms apart.
        // I haven't seen this personally in the webclient but I've seen the evidence in the server
        // logs. We're short on time right now and just need it working so rather than track down the
        // problem, I'm putting in a hack to suppress duplicate MSCL messages from going to the server.
        const lastCallRequestTime = this.lastCallRequestTimeByElevatorId[elevatorId];
        const MIN_WAIT_TIME_BETWEEN_CALL_REQUESTS = 1000;
        if (lastCallRequestTime && Date.now() - lastCallRequestTime < MIN_WAIT_TIME_BETWEEN_CALL_REQUESTS) {
            console.warn('Suppressing duplicate call request');
            return;
        }
        this.lastCallRequestTimeByElevatorId[elevatorId] = Date.now();

        this.sendEcs('MSCL', {
            elevatorId,
        }, enqueueIfNotOpen);
    }

    public ecsAnswer(callId: CallId, answer: string, offerSeq: number): void {
        this.sendEcs('ANSR', {
            callId,
            answer,
            offerSeq,
        });
    }

    public ecsPing(callId: CallId): void {
        this.sendEcs('PING', {
            callId,
        }, false); // don't queue pings
    }

    public ecsIceCandidate(callId: CallId, candidates: string[], offerSeq: number, done: boolean): void {
        this.sendEcs('ICES', {
            callId,
            candidates,
            offerSeq,
            done,
        });
    }

    public ecsChat(callId: CallId, text: string | null, messageId: string): void {
        this.sendEcs('CHAT', {
            callId,
            text,
            messageId,
        });
    }

    public ecsHelpComing(callId: CallId): void {
        this.sendEcs('HLPC', {
            callId,
        });
    }

    public ecsGoodbye(callId: CallId): void {
        this.sendEcs('GBYE', {
            callId,
        });
    }
}
